import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { NOTIFICATION_TYPES, realEntityName, type ProfileView } from '@pqrs/contracts';
import { Badge } from '@xiltrion/ui/badge';
import { Label } from '@xiltrion/ui/label';
import { Switch } from '@xiltrion/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@xiltrion/ui/tabs';
import { avisar } from '@xiltrion/ui/sonner';
import { Icono } from '@xiltrion/ui/icono';
import { useSession } from '@/app/session';
import { getThemeMode, setThemeMode, THEME_EVENT, type ThemeMode } from '@/app/theme';
import { Card, ErrorState, KeyValue, LoadingState, PageHeader, fmtDate } from '@/components/ui';
import { AVAILABILITY_LABEL, errorMessage } from '@/features/cases/lib';
import { useProfile, useUpdateProfile } from './api';
import { SecurityTab } from './SecurityTab';
import { SignatureTab } from './SignatureTab';

const TABS = ['cuenta', 'seguridad', 'firma', 'preferencias'] as const;

/** «Mi perfil»: cuenta (solo lectura), seguridad (contraseña, verificación en dos pasos, sesiones), cómo firma sus respuestas y preferencias. */
export function ProfilePage() {
  const { me } = useSession();
  const q = useProfile();
  const [sp, setSp] = useSearchParams();
  const tab = TABS.find((t) => t === sp.get('tab')) ?? 'cuenta';
  if (!me) return null;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Mi perfil" subtitle={me.email} />
      {q.isLoading ? <LoadingState rows={5} /> : q.isError || !q.data ? <ErrorState error={q.error} onRetry={() => void q.refetch()} title="No pudimos cargar tu perfil." /> : (
        <Tabs value={tab} onValueChange={(v) => { const n = new URLSearchParams(sp); n.set('tab', v); setSp(n, { replace: true }); }}>
          <TabsList variant="line" className="w-full justify-start">
            <TabsTrigger value="cuenta" className="flex-none gap-2"><Icono nombre="mdi:account-outline" /> Cuenta</TabsTrigger>
            <TabsTrigger value="seguridad" className="flex-none gap-2"><Icono nombre="mdi:shield-lock-outline" /> Seguridad</TabsTrigger>
            <TabsTrigger value="firma" className="flex-none gap-2"><Icono nombre="mdi:draw-pen" /> Firma</TabsTrigger>
            <TabsTrigger value="preferencias" className="flex-none gap-2"><Icono nombre="mdi:tune-variant" /> Preferencias</TabsTrigger>
          </TabsList>
          <TabsContent value="cuenta" className="pt-4"><AccountTab profile={q.data} /></TabsContent>
          <TabsContent value="seguridad" className="pt-4"><SecurityTab /></TabsContent>
          <TabsContent value="firma" className="pt-4"><SignatureTab profile={q.data} /></TabsContent>
          <TabsContent value="preferencias" className="pt-4"><PreferencesTab profile={q.data} /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function AccountTab({ profile }: { profile: ProfileView }) {
  const { me } = useSession();
  const avail = me?.availabilityStatus ?? 'AVAILABLE';
  return (
    <div className="grid gap-4 lg:grid-cols-2 items-start">
      <Card title="Tus datos">
        <KeyValue items={[
          { label: 'Nombre', value: profile.displayName },
          { label: 'Correo', value: profile.email },
          { label: 'Cargo', value: profile.jobTitle || '—' },
          { label: 'Entidad', value: realEntityName(me?.entity.name) ?? '—' },
          { label: 'Ingreso', value: profile.authProvider === 'local' ? 'Correo y contraseña' : 'Cuenta institucional' },
          { label: 'Último ingreso', value: profile.lastLoginAt ? fmtDate(profile.lastLoginAt) : '—' },
        ]} />
      </Card>
      <Card title="En la entidad">
        <KeyValue items={[
          { label: 'Roles', value: profile.roles.map((r) => r.name).join(', ') || '—' },
          { label: 'Áreas', value: profile.areas.length ? <span className="flex flex-wrap gap-1">{profile.areas.map((a) => { const does = [a.approves && 'aprueba', a.signs && 'firma'].filter(Boolean).join(' y '); return <Badge key={a.id} variant={does ? 'primary-light' : 'secondary'} size="sm">{a.name}{does ? ` · ${does}` : ''}</Badge>; })}</span> : '—' },
          { label: 'Disponibilidad', value: <span className="inline-flex items-center gap-1"><Icono nombre={avail === 'AVAILABLE' ? 'mdi:check-circle-outline' : 'mdi:clock-outline'} className={`size-4 ${avail === 'AVAILABLE' ? 'text-success' : 'text-warning'}`} aria-hidden />{AVAILABILITY_LABEL[avail] ?? avail}</span> },
        ]} />
      </Card>
      <p className="text-xs text-muted-foreground m-0 lg:col-span-2">Tu nombre, correo, cargo, roles y áreas los cambia un administrador; tu disponibilidad, un administrador.</p>
    </div>
  );
}

function PreferencesTab({ profile }: { profile: ProfileView }) {
  const update = useUpdateProfile();
  const [theme, setTheme] = useState<ThemeMode>(getThemeMode());
  useEffect(() => { const on = () => setTheme(getThemeMode()); window.addEventListener(THEME_EVENT, on); return () => window.removeEventListener(THEME_EVENT, on); }, []);
  const prefs = profile.preferences;
  const emailOn = (type: string) => prefs.notifications?.[type]?.email !== false;
  const save = (patch: Parameters<typeof update.mutate>[0], ok: string) => update.mutate(patch, { onSuccess: () => avisar.success(ok), onError: (e) => avisar.error('No pudimos guardar', { description: errorMessage(e) }) });
  const toggleEmail = (type: string, on: boolean) => save({ preferences: { notifications: { ...(prefs.notifications ?? {}), [type]: { email: on } } } }, on ? 'Te llegará también por correo' : 'Solo en la campana');
  return (
    <div className="grid gap-4 lg:grid-cols-2 items-start">
      <Card title="Avisos por correo">
        <p className="text-sm text-muted-foreground mt-0 mb-3">Todos los avisos llegan a la campana. Elige cuáles quieres recibir también en tu correo.</p>
        <ul className="list-none m-0 p-0 flex flex-col gap-2">
          {NOTIFICATION_TYPES.map((t) => (
            <li key={t.type} className="flex items-center justify-between gap-3">
              <Label htmlFor={`nt-${t.type}`} className="font-normal">{t.label}</Label>
              <Switch id={`nt-${t.type}`} checked={emailOn(t.type)} disabled={update.isPending} onCheckedChange={(v) => toggleEmail(t.type, !!v)} />
            </li>
          ))}
        </ul>
      </Card>
      <Card title="Pantalla">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="pf-dark" className="font-normal flex flex-col items-start gap-0.5"><span>Modo oscuro</span><span className="text-xs text-muted-foreground">Se guarda en este navegador.</span></Label>
            <Switch id="pf-dark" checked={theme === 'dark'} onCheckedChange={(v) => { const m: ThemeMode = v ? 'dark' : 'light'; setThemeMode(m); setTheme(m); }} />
          </div>
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="pf-keys" className="font-normal flex flex-col items-start gap-0.5"><span>Atajos de teclado</span><span className="text-xs text-muted-foreground">«/» para buscar, «a» para acciones en un expediente…</span></Label>
            <Switch id="pf-keys" checked={prefs.shortcuts !== false} disabled={update.isPending} onCheckedChange={(v) => save({ preferences: { shortcuts: !!v } }, v ? 'Atajos activados' : 'Atajos desactivados')} />
          </div>
        </div>
      </Card>
    </div>
  );
}
